import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { HOME_PATH } from "../vatar/env";
import hintService from "../Services/hintService";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  arrayUnion,
  getDocs,
  doc,
  limit,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase"; // Adjust path to your firebase config
import { checkPlayerStatus } from "../Services/checkPlayerStatus";
import { systemMessageService } from "../Services/systemMessageService";
import CoinBalance from "../Components/CoinBalance";
import analytics from "../Services/analyticsService";
import { PeekingMascot, roundMood } from "../Components/Mascot";
import Kbd from "../Components/Kbd";
import { finePointer } from "../utils/pointer";
import useConfirm from "../Components/Modals/useConfirm";

const PlayGame = () => {
  const [confirmDialog, ask] = useConfirm();
  const [roomData, setRoomData] = useState(null);
  const [roomDocId, setRoomDocId] = useState(null);
  const [gameLetter, setGameLetter] = useState("");
  const [timeLeft, setTimeLeft] = useState(45);
  // The live value, for code that runs after an await (e.g. a confirm dialog).
  const timeLeftRef = useRef(timeLeft);
  timeLeftRef.current = timeLeft;
  const [submitted, setSubmitted] = useState(false);
  const [players, setPlayers] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoryInputs, setCategoryInputs] = useState({});
  // Whether at least one category has a non-blank answer — gates the Submit
  // button (a fully blank manual submit is almost always a misclick).
  const hasAnyAnswer = useMemo(
    () => Object.values(categoryInputs).some((v) => v && v.trim()),
    [categoryInputs]
  );
  const isSpeedMode =
    roomData?.rounds?.[roomData.currentRound]?.isSpeedMode || false;
  const remainingLetters = 26 - (roomData?.playedLetters?.length || 0);
  const [hasAnnouncedRound, setHasAnnouncedRound] = useState(false);
  const [showStopGameOverlay, setShowStopGameOverlay] = useState(false);
  const [stopGameMessage, setStopGameMessage] = useState("");
  // Get current user info
  const currentUserId = localStorage.getItem("currentUserId");
  const currentRoomId = localStorage.getItem("currentRoomId");
  const currentPlayerName = localStorage.getItem("playerName");
  const [showAllLetters, setShowAllLetters] = useState(false);
  const currentRound = roomData?.currentRound || 1;
  const specialRounds = roomData?.specialRounds || [];
  // Same priority order as the timer effect's calculateTimeLeft.
  const roundTimeLimit = (() => {
    const roundData = roomData?.rounds?.[currentRound];
    if (roundData?.timeLimit !== undefined) return roundData.timeLimit;
    if (roomData?.hasCustomTimeLimit && roomData?.customTimeLimit) return roomData.customTimeLimit;
    return roundData?.isSpeedMode ? 20 : 45;
  })();
  const [showEarlyStopButton, setShowEarlyStopButton] = useState(false);
  const [earlyStopProcessing, setEarlyStopProcessing] = useState(false);
  const [earlyStopNotification, setEarlyStopNotification] = useState(null);
  const [showHostControls, setShowHostControls] = useState(false);
  const [canForceAdvance, setCanForceAdvance] = useState(false);
  const [missingPlayers, setMissingPlayers] = useState([]);
  const [hintResults, setHintResults] = useState({});
  const [showHintConfirm, setShowHintConfirm] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [hintAvailability, setHintAvailability] = useState({});
  const [showMemoryJoggerModal, setShowMemoryJoggerModal] = useState(null);
  const [showHintSheet, setShowHintSheet] = useState(null);

  // Helper to check if user is host
  const isHost = roomData?.hostId === currentUserId;

  const currentSpecialRound = specialRounds.find(
    (sr) => sr.round === currentRound
  );

  useEffect(() => {
    const loadHintAvailability = async () => {
      if (!currentUserId || !currentRoomId || !roomData?.currentRound) return;

      try {
        const memoryCheck = await hintService.checkHintAvailability(
          currentUserId,
          currentRoomId,
          currentRound,
          "memoryJogger"
        );

        const directCheck = await hintService.checkHintAvailability(
          currentUserId,
          currentRoomId,
          currentRound,
          "directAnswer"
        );

        setHintAvailability({
          memoryJogger: memoryCheck,
          directAnswer: directCheck,
        });

        console.log("✅ Hint availability loaded:", {
          memoryJogger: memoryCheck,
          directAnswer: directCheck,
        });
      } catch (error) {
        console.error("Error loading hint availability:", error);
      }
    };

    loadHintAvailability();
  }, [currentUserId, currentRoomId, currentRound, roomData]);
  // ☝️ Added roomData dependency

  // Memoize categories to prevent re-renders
  const categories = useMemo(
    () => roomData?.categories || ["name", "animal", "place", "thing"],
    [roomData?.categories]
  );

  const forceAdvanceToReview = async () => {
    if (!isHost || !roomDocId || !canForceAdvance) return;

    const ok = await ask({
      title: "Move everyone to review?",
      message: `${missingPlayers.map((p) => p.name).join(", ")} ${missingPlayers.length === 1 ? "hasn't" : "haven't"} submitted yet. Their answers will be left empty.`,
      confirmLabel: "Move to review",
    });
    if (!ok) return;

    try {
      const currentRound = roomData.currentRound || 1;
      const roomRef = doc(db, "rooms", roomDocId);

      // Re-read the room fresh right before deciding who's "missing" — the local
      // roomData snapshot can lag a player's own just-landed submission by a beat,
      // and this fully REPLACES the submissions array, so a stale read here could
      // silently overwrite a real answer with an empty one if a player's own
      // timeout auto-submit raced this action.
      const freshSnap = await getDoc(roomRef);
      const freshRoomData = freshSnap.data();
      const existingSubmissions =
        freshRoomData?.rounds?.[currentRound]?.submissions ||
        roomData.rounds[currentRound]?.submissions ||
        [];
      const submissionMap = new Map();

      // Keep only the latest submission per player
      existingSubmissions.forEach((submission) => {
        const existing = submissionMap.get(submission.userId);
        if (
          !existing ||
          new Date(submission.submittedAt) > new Date(existing.submittedAt)
        ) {
          submissionMap.set(submission.userId, submission);
        }
      });

      // Create empty submissions for players who haven't submitted at all
      const submittedPlayerIds = Array.from(submissionMap.keys());
      const actuallyMissingPlayers = roomData.players.filter(Boolean).filter(
        (player) => !submittedPlayerIds.includes(player.id)
      );

      const emptySubmissions = actuallyMissingPlayers.map((player) => ({
        userId: player.id,
        playerName: player.name,
        answers: categories.reduce((acc, category) => {
          acc[category] = ""; // Empty answer
          return acc;
        }, {}),
        submittedAt: new Date(),
        letter: gameLetter,
        forcedSubmission: true,
        missedDeadline: true,
      }));

      // Add empty submissions to the map
      emptySubmissions.forEach((submission) => {
        submissionMap.set(submission.userId, submission);
      });

      // Convert back to array - now guaranteed to be unique
      const allUniqueSubmissions = Array.from(submissionMap.values());

      await updateDoc(roomRef, {
        [`rounds.${currentRound}.submissions`]: allUniqueSubmissions,
        [`rounds.${currentRound}.timeExpired`]: true,
        [`rounds.${currentRound}.forcedByHost`]: true,
        [`rounds.${currentRound}.forcedFromPlay`]: true, // DELETE_FLAG: Mark this was forced from play phase
        [`rounds.${currentRound}.forcedAt`]: new Date(),
      });

      // Send system message about forced advance
      try {
        await systemMessageService.hostForcedAdvance(
          roomDocId,
          currentRound,
          actuallyMissingPlayers.map((p) => p.name)
        );
      } catch (error) {
        console.warn("Failed to send forced advance message:", error);
      }

      console.log(
        "Host forced advance to review phase with unique submissions:",
        allUniqueSubmissions.length
      );
    } catch (error) {
      console.error("Error forcing advance:", error);
      alert("Failed to force advance. Please try again.");
    }
  };

  // Update category inputs when categories change
  useEffect(() => {
    if (categories) {
      // Initialize any missing categories with empty strings
      setCategoryInputs((prev) => {
        const newInputs = { ...prev };
        categories.forEach((category) => {
          if (newInputs[category] === undefined) {
            newInputs[category] = "";
          }
        });
        return newInputs;
      });
    }
  }, [categories]);

  // Load room data and set up real-time listener
  useEffect(() => {
    if (!currentRoomId) {
      // If no room, redirect to home
      window.location.href = HOME_PATH;
      return;
    }

    const unsubscribePlayerCheck = checkPlayerStatus(
      currentRoomId,
      currentUserId
    );

    // First, get the document ID for direct reference
    const getDocId = async () => {
      const q = query(
        collection(db, "rooms"),
        where("roomId", "==", currentRoomId),
        limit(1)
      );

      try {
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docId = querySnapshot.docs[0].id;
          setRoomDocId(docId);

          // Load from localStorage first if available, for instant display
          const cachedState = localStorage.getItem("gameState");
          if (cachedState) {
            try {
              const parsedState = JSON.parse(cachedState);
              setGameLetter(parsedState.currentLetter || "");
            } catch (e) {
              console.error("Error parsing cached game state:", e);
            }
          }

          // Now set up a more efficient listener on the specific document
          const roomRef = doc(db, "rooms", docId);
          const docUnsubscribe = onSnapshot(roomRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = { id: docSnapshot.id, ...docSnapshot.data() };
              setRoomData(data);
              setPlayers(data.players || []);
              setGameLetter(data.currentLetter || "");

              // Check current round and submission status
              const currentRound = data.currentRound || 1;
              const roundData = data.rounds?.[currentRound];

              if (roundData) {
                // Check if current user already submitted
                const userSubmission = roundData.submissions?.find(
                  (sub) => sub.userId === currentUserId
                );
                setSubmitted(!!userSubmission);

                // Count submissions for display
                const submissionCount = roundData.submissions?.length || 0;
                const totalPlayers = data.players?.length || 0;
                setSubmissionCounts({
                  submitted: submissionCount,
                  total: totalPlayers,
                  remaining: totalPlayers - submissionCount,
                });
              }

              setLoading(false);
            }
          });

          return docUnsubscribe;
        } else {
          console.error("Room not found");
          window.location.href = HOME_PATH;
        }
      } catch (error) {
        console.error("Error getting room:", error);
        setLoading(false);
      }
    };

    const unsubscribePromise = getDocId();

    return () => {
      if (unsubscribePlayerCheck) unsubscribePlayerCheck();
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [currentRoomId, currentUserId]);

  useEffect(() => {
    if (!roomData?.rounds?.[currentRound]?.submissions || !submitted) return;

    const submissions = roomData.rounds[currentRound].submissions;

    // Check if current user was the first to submit
    if (submissions.length === 1 && submissions[0].userId === currentUserId) {
      setShowEarlyStopButton(true);
    } else {
      setShowEarlyStopButton(false);
    }
  }, [
    roomData?.rounds?.[currentRound]?.submissions,
    submitted,
    currentUserId,
    currentRound,
  ]);

  // Add this useEffect to monitor for early stop triggers from other players
  useEffect(() => {
    if (!roomData?.rounds?.[currentRound]) return;

    const roundData = roomData.rounds[currentRound];

    // Check if someone triggered early stop
    if (
      roundData.earlyStopTriggeredBy &&
      roundData.earlyStopTriggeredBy !== currentUserId
    ) {
      const triggerPlayer = roomData.players?.find(
        (p) => p.id === roundData.earlyStopTriggeredBy
      );
      const playerName = triggerPlayer?.name || "Someone";

      setEarlyStopNotification(`${playerName} ended the round!`);
      setShowEarlyStopButton(false);
      setShowStopGameOverlay(true);
      // Clear notification after 3 seconds
      setTimeout(() => setShowStopGameOverlay(false), 3000);
    }
  }, [
    roomData?.rounds?.[currentRound]?.earlyStopTriggeredBy,
    currentUserId,
    roomData?.players,
  ]);

  // Add the early stop handler function
  const handleEarlyStop = async () => {
    if (!roomDocId || earlyStopProcessing || !showEarlyStopButton) return;

    const ok = await ask({
      title: "End the round for everyone?",
      message:
        timeLeft <= 5
          ? "The round ends now for every player."
          : "Everyone gets 5 more seconds to finish, then the round ends.",
      confirmLabel: "End round",
    });
    if (!ok) return;

    setEarlyStopProcessing(true);
    setShowEarlyStopButton(false);

    try {
      // The confirm dialog doesn't stop the clock, and someone else may have
      // ended the round while it was open: read the round fresh, not from
      // the roomData captured when the button was tapped.
      const roomRef = doc(db, "rooms", roomDocId);
      const fresh = (await getDoc(roomRef)).data()?.rounds?.[currentRound];
      if (fresh?.earlyStopTriggeredBy) return;

      // Calculate what the new timeLimit should be
      const originalTimeLimit = fresh?.timeLimit || 45;
      const currentTimeLeft = timeLeftRef.current;
      const elapsedTime = originalTimeLimit - currentTimeLeft;

      let newTimeLimit;
      if (currentTimeLeft <= 5) {
        newTimeLimit = elapsedTime; // End immediately (set to current elapsed time)
      } else {
        newTimeLimit = elapsedTime + 5; // Add 5 seconds to elapsed time
      }

      await updateDoc(roomRef, {
        [`rounds.${currentRound}.timeLimit`]: newTimeLimit,
        [`rounds.${currentRound}.earlyStopTriggeredBy`]: currentUserId,
        [`rounds.${currentRound}.earlyStopTriggeredAt`]: new Date(),
      });

      setEarlyStopNotification("You ended the round early!");
      setTimeout(() => setEarlyStopNotification(null), 3000);
    } catch (error) {
      console.error("Error triggering early stop:", error);
      setShowEarlyStopButton(true);
    } finally {
      setEarlyStopProcessing(false);
    }
  };

  // Monitor for players who can be force-advanced
  useEffect(() => {
    if (!roomData || !isHost || submitted) return;

    const currentRound = roomData.currentRound || 1;
    const roundData = roomData.rounds?.[currentRound];

    if (!roundData?.submissions) {
      setCanForceAdvance(false);
      setMissingPlayers([]);
      setShowHostControls(false);
      return;
    }

    const submittedPlayerIds = roundData.submissions.map((sub) => sub.userId);
    const missing = roomData.players.filter(Boolean).filter(
      (player) => player.id !== currentUserId && !submittedPlayerIds.includes(player.id)
    );

    setMissingPlayers(missing);

    // Always allow force advance if there are missing players (no timer restriction)
    setCanForceAdvance(missing.length > 0);
    setShowHostControls(missing.length > 0);
  }, [
    roomData?.rounds?.[currentRound]?.submissions,
    roomData?.players,
    isHost,
    submitted,
    currentRound,
  ]);

  // Handle input changes efficiently
  const handleInputChange = useCallback((category, value) => {
    setCategoryInputs((prev) => ({
      ...prev,
      [category]: value,
    }));
  }, []);

  // Submit function - optimized to use direct doc reference
  const submitAnswers = useCallback(
    async (isForced = false) => {
      if (submitted || !roomDocId) return; // Prevent double submission

      // A manual submit with every field left blank is almost always a misclick —
      // block it. Auto-submit on timeout (isForced) still goes through with
      // whatever's there, including nothing, since the round has to move on.
      if (!isForced && !hasAnyAnswer) {
        alert("Fill in at least one answer before submitting.");
        return;
      }

      try {
        setSubmitted(true); // Disable form immediately

        // Create submission object with explicit structure
        const submission = {
          userId: currentUserId,
          playerName: currentPlayerName,
          answers: { ...categoryInputs }, // Using spread to ensure we create a new object
          submittedAt: new Date(),
          letter: gameLetter,
          forcedSubmission: isForced,
        };

        // Update the room document in Firebase
        const roomRef = doc(db, "rooms", roomDocId);
        const currentRound = roomData.currentRound || 1;
        const submissionPath = `rounds.${currentRound}.submissions`;

        // Update Firebase
        await updateDoc(roomRef, {
          [submissionPath]: arrayUnion(submission),
        });

        const categoriesAnswered = Object.values(categoryInputs).filter(
          (v) => v && v.trim()
        ).length;
        analytics.track("round_submitted", {
          room_id: roomDocId,
          round_number: currentRound,
          categories_answered: categoriesAnswered,
          time_remaining_sec: isForced ? 0 : timeLeft,
        });

        console.log("Submission successful!");
      } catch (error) {
        console.error("Error submitting answers:", error);
        alert("Failed to submit answers. Please try again.");
        setSubmitted(false); // Re-enable form on error
      }
    },
    [
      submitted,
      roomDocId,
      categoryInputs,
      hasAnyAnswer,
      gameLetter,
      currentUserId,
      currentPlayerName,
      roomData,
      timeLeft,
    ]
  );

  useEffect(() => {
    if (!roomData || submitted) return;

    const currentRound = roomData.currentRound || 1;
    const roundData = roomData.rounds?.[currentRound];

    if (!roundData?.startedAt) return;

    // DELETE_FLAG: Don't start timer if round already expired (auto-advanced by Firebase)
    if (roundData.timeExpired || roundData.autoAdvanced) {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      // Handle different timestamp formats from Firebase
      let roundStartMs;
      if (roundData.startedAt.toDate) {
        roundStartMs = roundData.startedAt.toDate().getTime();
      } else if (roundData.startedAt instanceof Date) {
        roundStartMs = roundData.startedAt.getTime();
      } else {
        roundStartMs = new Date(roundData.startedAt).getTime();
      }

      // 🔥 Get time limit from multiple sources in priority order
      let timeLimit = 45; // Default fallback

      // 1. Round-specific time limit (highest priority)
      if (roundData.timeLimit !== undefined) {
        timeLimit = roundData.timeLimit;
      }
      // 2. Room custom time limit (if purchased)
      else if (roomData.hasCustomTimeLimit && roomData.customTimeLimit) {
        timeLimit = roomData.customTimeLimit;
      }
      // 3. Speed mode override (for final rounds)
      else if (roundData.isSpeedMode) {
        timeLimit = 20;
      }

      const now = new Date().getTime();
      const elapsed = Math.floor((now - roundStartMs) / 1000);
      const remaining = Math.max(0, timeLimit - elapsed);
      return remaining;
    };

    // Set initial time
    const initialTime = calculateTimeLeft();
    setTimeLeft(initialTime);

    // Update timer every second
    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        // Auto-submit when time runs out (forced submission)
        submitAnswers(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    roomData?.rounds?.[currentRound]?.timeLimit,
    roomData?.rounds?.[currentRound]?.startedAt,
    roomData?.rounds?.[currentRound]?.timeExpired,
    roomData?.rounds?.[currentRound]?.autoAdvanced,
    roomData?.hasCustomTimeLimit,
    roomData?.customTimeLimit,
    submitted,
    submitAnswers,
    currentRound,
  ]);
  const getTimerTypeIndicator = () => {
    const currentRound = roomData?.currentRound || 1;
    const roundData = roomData?.rounds?.[currentRound];

    // Check if custom timer is active
    if (roomData?.hasCustomTimeLimit && roomData?.customTimeLimit) {
      return (
        <div className="text-center mb-2">
          {/* <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Custom Timer
          </span> */}
        </div>
      );
    }

    // Check if speed mode
    if (roundData?.isSpeedMode) {
      return (
        <div className="text-center mb-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <span className="mr-1">⚡</span>
            Speed Mode
          </span>
        </div>
      );
    }

    return null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // End game function for host
  const endGame = async () => {
    if (!isHost || !roomDocId) return;

    const ok = await ask({
      title: "End the game for everyone?",
      message: "This round can still be submitted, then the game ends and final scores are shown.",
      confirmLabel: "End game",
    });
    if (ok) {
      try {
        const roomRef = doc(db, "rooms", roomDocId);
        await updateDoc(roomRef, {
          gameStatus: "ended",
          gameEndedAt: new Date(),
          gameEndedBy: currentUserId,
        });
      } catch (error) {
        console.error("Error ending game:", error);
        alert("Failed to end game. Please try again.");
      }
    }
  };

  // Calculate total scores for mini scoreboard
  const getTotalScores = useCallback(() => {
    if (!roomData?.rounds || !roomData?.players) return [];

    const playerScores = {};

    // Initialize scores for all players
    roomData.players.forEach((player) => {
      playerScores[player.id] = {
        name: player.name,
        total: 0,
      };
    });

    // Sum up scores from all completed rounds
    Object.values(roomData.rounds).forEach((round) => {
      if (round.scores) {
        Object.entries(round.scores).forEach(([userId, categoryScores]) => {
          if (playerScores[userId]) {
            playerScores[userId].total += categoryScores.total || 0;
          }
        });
      }
    });

    // Convert to array and sort by total score
    return Object.values(playerScores).sort((a, b) => b.total - a.total);
  }, [roomData?.rounds, roomData?.players]);

  const inputRefs = useRef({});

  useEffect(() => {
    if (loading || submitted) return;
    if (!window.matchMedia("(min-width: 1024px) and (pointer: fine)").matches) return;
    inputRefs.current[categories[0]]?.focus();
  }, [loading, submitted, gameLetter, categories]);

  // Desktop only: Enter moves to the next box, and submits from the last one.
  const handleBoxKeyDown = (e, index) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    if (!window.matchMedia("(min-width: 1024px)").matches) return;
    e.preventDefault();
    const nextCategory = categories[index + 1];
    if (nextCategory) inputRefs.current[nextCategory]?.focus();
    else if (hasAnyAnswer && !submitted) submitAnswers(false);
  };

  // Generate alphabet grid with highlight
  const generateAlphabetGrid = useCallback(() => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const playedLetters = roomData?.playedLetters || [];
    const specialRounds = roomData?.specialRounds || [];

    // Create a map of special round numbers to letters for easy lookup
    const specialLetters = new Set();
    specialRounds.forEach((sr) => {
      // Get the letter for this special round number
      if (roomData?.playedLetters && roomData.playedLetters[sr.round - 1]) {
        specialLetters.add(roomData.playedLetters[sr.round - 1]);
      }
    });

    return letters.map((letter, index) => {
      const isCurrentLetter = letter === gameLetter;
      const isPlayedLetter = playedLetters.includes(letter);
      const isSpecialLetter = specialLetters.has(letter);
      const roundNumber = index + 1; // A=1, B=2, etc.
      const isUpcomingSpecial =
        specialRounds.some((sr) => sr.round === roundNumber) && !isPlayedLetter;

      return (
        <div
          key={letter}
          className={`relative px-4 py-2 md:p-3 text-center flex items-center justify-center border rounded-lg transition-all duration-300 ${isCurrentLetter
              ? isUpcomingSpecial || isSpecialLetter
                ? "border-2 border-purple-500 bg-gradient-to-br from-purple-100 to-pink-100 text-purple-800 font-bold shadow-lg transform scale-110 ring-2 ring-purple-200"
                : "border-2 border-green-500 bg-green-100 text-green-800 font-bold shadow-md transform scale-110"
              : isPlayedLetter
                ? isSpecialLetter
                  ? "bg-gradient-to-br from-purple-200 to-pink-200 text-purple-700 border-purple-300 shadow-sm"
                  : "bg-gray-200 text-gray-400 border-gray-300"
                : isUpcomingSpecial
                  ? "bg-gradient-to-br from-rose-100 to-orange-100 text-rose-800 border-rose-300 hover:from-rose-200 hover:to-orange-200 shadow-sm ring-1 ring-rose-200"
                  : "bg-red-100 text-red-800 border-red-200 hover:bg-red-200"
            }`}
        >
          <span className="text-lg font-semibold">{letter}</span>

          {/* Subtle diamond indicator for upcoming special rounds */}
          {isUpcomingSpecial && !isCurrentLetter && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-rose-400 to-orange-400 transform rotate-45 shadow-sm"></div>
          )}

          {/* Current special letter gets a glowing dot */}
          {isCurrentLetter && (isUpcomingSpecial || isSpecialLetter) && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full shadow-md animate-pulse"></div>
          )}

          {/* Played letter indicator */}
          {isPlayedLetter && !isCurrentLetter && (
            <div className="md:absolute hidden top-1 right-1 text-gray-500 text-sm">
              {isSpecialLetter ? "💎" : "✗"}
            </div>
          )}
        </div>
      );
    });
  }, [gameLetter, roomData?.playedLetters, roomData?.specialRounds]);

  // Get list of players who have submitted
  const getSubmittedPlayers = useCallback(() => {
    if (!roomData?.rounds?.[roomData.currentRound]?.submissions) return [];

    const submissions = roomData.rounds[roomData.currentRound].submissions;
    return submissions.map((sub) => sub.playerName);
  }, [roomData]);

  // Memoize values to prevent unnecessary re-renders
  const submittedPlayers = useMemo(
    () => getSubmittedPlayers(),
    [getSubmittedPlayers]
  );
  const totalScores = useMemo(() => getTotalScores(), [getTotalScores]);

  if (loading) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 flex justify-center items-center">
        <div className="text-red-800 text-xl">Loading game...</div>
      </div>
    );
  }

  const isHintUsed = (category, hintType) => {
    return !!hintResults[`${category}-${hintType}`];
  };

  const handleSelectHint = async (category, hintType) => {
    try {
      setHintLoading(true);

      const result = await hintService.useHint(
        currentUserId,
        currentRoomId,
        currentRound,
        category,
        hintType
      );

      if (hintType === "memoryJogger") {
        setShowHintSheet(null);
        setShowMemoryJoggerModal({
          category,
          hint: result.hint,
        });
      } else if (hintType === "directAnswer") {
        handleInputChange(category, result.hint);
        setShowHintSheet(null);
      }

      setHintResults((prev) => ({
        ...prev,
        [`${category}-${hintType}`]: result,
      }));

      const memoryCheck = await hintService.checkHintAvailability(
        currentUserId,
        currentRoomId,
        currentRound,
        "memoryJogger"
      );

      const directCheck = await hintService.checkHintAvailability(
        currentUserId,
        currentRoomId,
        currentRound,
        "directAnswer"
      );

      setHintAvailability({
        memoryJogger: memoryCheck,
        directAnswer: directCheck,
      });
    } catch (error) {
      console.error("Error using hint:", error);
      alert(error.message || "Failed to use hint. Please try again.");
    } finally {
      setHintLoading(false);
    }
  };
  const handleHintClick = (category, hintType) => {
    if (submitted) return;

    setShowHintConfirm({ category, hintType });
  };
  const getCostLabel = (hintType) => {
    const availability = hintAvailability[hintType];
    if (!availability) return "...";

    if (availability.hasFreeHints) {
      return `FREE - ${availability.freeHintsCount} left`;
    }

    return `${availability.cost} coins`;
  };

  const canUseHint = (hintType) => {
    const availability = hintAvailability[hintType];

    // If availability hasn't loaded yet, allow clicking (optimistic)
    if (!availability) return true;

    return availability.hasFreeHints || availability.hasEnoughCoins;
  };

  const handleNeedHelpClick = (category) => {
    if (submitted) return;
    setShowHintSheet(category);
  };

  const playerList = (roomData?.players || []).filter(Boolean);
  const letterChips = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter, index) => {
    const playedLetters = roomData?.playedLetters || [];
    const isCurrent = letter === gameLetter;
    const isPlayed = playedLetters.includes(letter);
    const isUpcomingSpecial =
      (roomData?.specialRounds || []).some((sr) => sr.round === index + 1) && !isPlayed;
    return (
      <span
        key={letter}
        className={`h-7 rounded-md border flex items-center justify-center text-xs font-bold ${isCurrent
            ? "bg-green-100 border-green-500 text-green-800 ring-1 ring-green-300"
            : isPlayed
              ? "bg-gray-100 border-gray-200 text-gray-400 line-through"
              : isUpcomingSpecial
                ? "bg-rose-100 border-rose-300 text-rose-800"
                : "bg-red-50 border-red-100 text-red-800"
          }`}
      >
        {letter}
      </span>
    );
  });
  const timeLeftPct =
    roundTimeLimit > 0 ? Math.max(0, Math.min(100, (timeLeft / roundTimeLimit) * 100)) : 0;

  return (
    <div className="min-h-screen bg-orange-50 container px-6 lg:mx-auto lg:max-w-6xl">
      {/* Header */}
      <div className="flex justify-between items-center py-4">
        <div>
          <h1 className="text-2xl font-bold text-red-800">NameIt!</h1>
          <p className="text-sm text-gray-500">Playing as {currentPlayerName}</p>
        </div>
        <div className="flex items-center gap-2">
          <CoinBalance />
          {isHost && roomData?.gameStatus !== "ended" && (
            <button
              onClick={endGame}
              className="bg-white border border-gray-300 text-gray-700 text-sm font-semibold min-h-[44px] px-4 rounded-lg hover:bg-gray-50 transition duration-300"
            >
              End Game
            </button>
          )}
        </div>
      </div>

      {/* Game Status Notification */}
      {roomData?.gameStatus === "ended" && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          <strong>Game Ended:</strong> Host has ended the game. You can still
          submit your answers for this round.
        </div>
      )}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6 lg:items-start">
      <div className="min-w-0">
      {/* Alphabet Grid - Collapsible */}
      <div className="mb-4 lg:hidden">
        <div className="grid grid-cols-8 gap-2 mb-2">
          {(showAllLetters ? generateAlphabetGrid() : generateAlphabetGrid().slice(0, 16))}
        </div>
        <div className="flex justify-center">
          <button
            onClick={() => setShowAllLetters(!showAllLetters)}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            {showAllLetters ? 'Hide letters ▲' : 'Show all letters ▼'}
          </button>
        </div>
      </div>

      <PeekingMascot
        mood={roundMood({ secondsLeft: timeLeft, totalSeconds: roundTimeLimit, submitted })}
        className="w-full mb-6 mx-auto max-w-2xl lg:max-w-none"
      >
      <div className="w-full mx-auto bg-white rounded-2xl shadow-lg p-6 max-w-2xl lg:max-w-none lg:p-8">
        <div className="hidden lg:flex items-center justify-between gap-6 mb-2">
          <p className="text-lg text-gray-600">
            Fill in each box with a word starting with{" "}
            <strong className="text-red-700">{gameLetter}</strong>.
          </p>
          <p className={`shrink-0 text-right text-2xl font-extrabold tabular-nums ${isSpeedMode || timeLeft <= 10 ? "text-red-600" : "text-gray-800"}`}>
            {formatTime(timeLeft)} left
          </p>
        </div>
        {/* Speed Mode Alert (Priority) */}
        {isSpeedMode && (
          <div className="bg-red-50 border border-red-400 rounded-lg p-2 mb-3 shadow-sm">
            <div className="text-center">
              <h3 className="text-base font-semibold text-red-700">
                SPEED MODE
              </h3>
              <p className="text-red-600 text-xs">
                {remainingLetters} letters left
              </p>
            </div>
          </div>
        )}

        {/* Current Letter and Timer with Special Round Badge */}
        <div className="flex justify-center items-center relative lg:hidden">
          <p className="font-black text-center py-3 text-5xl text-red-700 flex-1" aria-label={`Letter ${gameLetter}`}>
            {gameLetter}
          </p>

          {/* Special Round Badge - Subtle indicator */}
          {currentSpecialRound && (
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                SPECIAL
              </div>
            </div>
          )}
        </div>
        {getTimerTypeIndicator()}
        <p
          className={`text-center text-lg lg:hidden ${isSpeedMode ? "text-red-600 font-bold" : ""
            }`}
        >
          {formatTime(timeLeft)} seconds left
          {isSpeedMode && <span className="ml-2 animate-pulse">⚡</span>}
        </p>
        {earlyStopNotification && (
          <div className="text-center text-red-600 py-3 font-semibold">
            {earlyStopNotification}
          </div>
        )}
        {/* Form Fields (Dynamic based on room categories) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 lg:gap-x-6 lg:gap-y-5">
          {categories.map((category, index) => (
            <div key={category}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold capitalize text-sm">{category}</p>
                <button
                  onClick={() => handleNeedHelpClick(category)}
                  disabled={submitted || timeLeft === 0}
                  className={`text-lg w-11 h-11 -my-2 -mr-2 flex items-center justify-center rounded-full transition ${submitted || timeLeft === 0
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-amber-50"
                    }`}
                  title="Need help?"
                  aria-label={`Get a hint for ${category}`}
                >
                  💡
                </button>
              </div>
              <input
                type="text"
                ref={(el) => { inputRefs.current[category] = el; }}
                onKeyDown={(e) => handleBoxKeyDown(e, index)}
                className="p-3 lg:p-4 border-2 border-red-200 rounded-lg w-full text-sm lg:text-lg lg:font-semibold focus:border-red-400 focus:outline-none"
                onChange={(e) => handleInputChange(category, e.target.value)}
                value={categoryInputs[category] || ""}
                disabled={submitted}
                placeholder={`${category.charAt(0).toUpperCase() + category.slice(1)
                  } starting with ${gameLetter}...`}
              />
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-6">
          <button
            onClick={() => submitAnswers(false)}
            disabled={submitted || !hasAnyAnswer}
            className={`w-full px-6 py-4 rounded-xl font-bold text-white text-lg transition-colors duration-300 ${submitted || !hasAnyAnswer
                ? "bg-gray-400 cursor-not-allowed"
                : "btn-press bg-red-600 hover:bg-red-700"
              }`}
          >
            {submitted ? "✓ Submitted!" : "Submit Answers"}
          </button>
          {finePointer && !submitted && (
            <p className="hidden lg:block mt-3 text-center text-xs text-gray-500">
              <Kbd>Enter</Kbd> moves to the next box · <Kbd>Enter</Kbd> in the last box submits
            </p>
          )}
        </div>
        {showEarlyStopButton && (
          <div className="mt-8 pt-4 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-600 mb-2">Done early? You can stop the clock for everyone.</p>
            <button
              onClick={handleEarlyStop}
              disabled={earlyStopProcessing}
              className={`w-full px-6 py-3 rounded-xl font-semibold border-2 transition duration-300 ${earlyStopProcessing
                  ? "border-gray-300 text-gray-500 cursor-not-allowed"
                  : "border-orange-500 text-orange-700 bg-white hover:bg-orange-50"
                }`}
            >
              {earlyStopProcessing ? "Ending Round..." : "End Round for everyone"}
            </button>
          </div>
        )}
        {earlyStopNotification && (
          <div className="fixed top-8 text-center left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 font-semibold">
            {earlyStopNotification}
          </div>
        )}
        {/* Submission Status */}
        <div className="mt-6 text-center lg:hidden">
          {submitted ? (
            <p className="text-green-600 font-semibold">
              ✅ You have submitted your answers!
            </p>
          ) : null}

          <p className="mt-2 text-gray-700">
            {submittedPlayers.length > 0 && (
              <>
                <span className="font-semibold">Submitted: </span>
                {submittedPlayers.join(", ")}
              </>
            )}
          </p>

          <p className="mt-2 text-sm text-gray-600">
            {submissionCounts.submitted} of {submissionCounts.total} players
            have submitted
            {submissionCounts.remaining > 0 && (
              <span>, {submissionCounts.remaining} remaining</span>
            )}
          </p>
        </div>

        {/* Special Round Hint - Bottom tooltip */}
        {currentSpecialRound && (
          <div className="mt-4 text-center">
            <p className="text-purple-600 text-xs italic">
              💫 Something special will happen this round...
            </p>
          </div>
        )}
      </div>
      </PeekingMascot>
      </div>

      <aside className="hidden lg:block lg:sticky lg:top-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-100 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Your letter</p>
              <p className="text-8xl leading-none font-black text-red-700" aria-label={`Letter ${gameLetter}`}>
                {gameLetter}
              </p>
            </div>
            {currentSpecialRound && (
              <span className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                SPECIAL
              </span>
            )}
          </div>
          <div className="mt-4 h-2 rounded-full bg-red-100 overflow-hidden" aria-hidden="true">
            <div className="h-full bg-red-600 transition-all duration-1000 ease-linear" style={{ width: `${timeLeftPct}%` }} />
          </div>
          <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">Letters so far</p>
          <div className="grid grid-cols-9 gap-1" aria-label="Letters used so far">
            {letterChips}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">
            {submittedPlayers.length} of {playerList.length} submitted
          </p>
          {playerList.map((player) => {
            const done = submittedPlayers.includes(player.name);
            return (
              <div key={player.id || player.name} className="flex items-center justify-between py-1.5 text-[15px]">
                <span className="font-semibold">
                  {player.name}
                  {player.id === currentUserId && <span className="font-normal text-gray-500"> (you)</span>}
                </span>
                <span className={`text-sm font-semibold ${done ? "text-green-700" : "text-gray-500"}`}>
                  {done ? "Submitted" : "Typing…"}
                </span>
              </div>
            );
          })}
        </div>

        {isHost && canForceAdvance && (
          <div className="text-center">
            <button
              onClick={forceAdvanceToReview}
              className="w-full bg-orange-600 text-white px-6 py-3 rounded-xl hover:bg-orange-700 transition duration-300 font-semibold"
            >
              Move to Review
            </button>
            <p className="text-xs text-gray-600 mt-2">Missing players will get empty submissions</p>
          </div>
        )}
      </aside>
      </div>

      {/* Host Controls - Move to Review */}
      {isHost && canForceAdvance && (
        <div className="text-center mb-6 max-w-2xl mx-auto lg:hidden">
          <button
            onClick={forceAdvanceToReview}
            className="bg-orange-600 text-white px-6 py-3 rounded-xl hover:bg-orange-700 transition duration-300 font-semibold"
          >
            Move to Review
          </button>
          <p className="text-xs text-gray-600 mt-2">
            Missing players will get empty submissions
          </p>
        </div>
      )}

      {confirmDialog}

      {showHintSheet && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowHintSheet(null)}
        >
          <div
            className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
            <h3 className="text-xl font-bold mb-1 capitalize text-center">
              Hints for {showHintSheet}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Choose a hint type
            </p>

            {/* Coin Balance Display */}
            <div className="flex justify-center mb-4">
              <CoinBalance size="normal" />
              {(!hintAvailability.memoryJogger?.hasEnoughCoins || !hintAvailability.directAnswer?.hasEnoughCoins) && (
                <p className="text-xs text-red-600 mt-2 text-center">
                  Not enough coins? You can buy more in the Review phase!
                </p>
              )}
            </div>

            <div className="space-y-3">
              {/* Memory Jogger Option */}
              <button
                onClick={() => handleSelectHint(showHintSheet, "memoryJogger")}
                disabled={
                  hintLoading ||
                  !canUseHint("memoryJogger") ||
                  isHintUsed(showHintSheet, "memoryJogger")
                }
                className={`w-full p-4 rounded-xl border-2 text-left transition ${hintLoading ||
                    !canUseHint("memoryJogger") ||
                    isHintUsed(showHintSheet, "memoryJogger")
                    ? "bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed"
                    : "bg-yellow-50 border-yellow-300 hover:bg-yellow-100 active:scale-95"
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">💡</span>
                      <span className="font-bold text-lg">Memory Jogger</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Get a clue to jog your memory
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-semibold ${hintAvailability.memoryJogger?.hasFreeHints
                            ? "text-green-600"
                            : "text-orange-600"
                          }`}
                      >
                        {getCostLabel("memoryJogger")}
                      </span>
                      {isHintUsed(showHintSheet, "memoryJogger") && (
                        <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded-full">
                          Used ✓
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>

              {/* Direct Answer Option */}
              <button
                onClick={() => handleSelectHint(showHintSheet, "directAnswer")}
                disabled={
                  hintLoading ||
                  !canUseHint("directAnswer") ||
                  isHintUsed(showHintSheet, "directAnswer")
                }
                className={`w-full p-4 rounded-xl border-2 text-left transition ${hintLoading ||
                    !canUseHint("directAnswer") ||
                    isHintUsed(showHintSheet, "directAnswer")
                    ? "bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed"
                    : "bg-orange-50 border-orange-300 hover:bg-orange-100 active:scale-95"
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">🎯</span>
                      <span className="font-bold text-lg">Direct Answer</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Get the answer auto-filled
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-sm font-semibold ${hintAvailability.directAnswer?.hasFreeHints
                            ? "text-green-600"
                            : "text-orange-600"
                          }`}
                      >
                        {getCostLabel("directAnswer")}
                      </span>
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                        -1 point penalty
                      </span>
                      {isHintUsed(showHintSheet, "directAnswer") && (
                        <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded-full">
                          Used ✓
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowHintSheet(null)}
              disabled={hintLoading}
              className="w-full mt-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition"
            >
              {hintLoading ? "Loading..." : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Memory Jogger Result Modal */}
      {showMemoryJoggerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="text-center mb-4">
              <span className="text-4xl">💡</span>
            </div>
            <h3 className="text-xl font-bold mb-2 capitalize text-center">
              {showMemoryJoggerModal.category} Hint
            </h3>
            <div className="flex justify-center mb-4">
              <CoinBalance size="normal" />
            </div>
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-4">
              <p className="text-lg text-center font-medium">
                {showMemoryJoggerModal.hint}
              </p>
            </div>
            <button
              onClick={() => setShowMemoryJoggerModal(null)}
              className="w-full px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayGame;
